<template>
  <NuxtLayout name="404">
    <HeaderDefault />

    <main
      class="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center"
    >
      <div class="max-w-5xl mx-auto px-4 py-16 text-center">
        <div class="mb-10">
          <client-only>
            <Vue3Lottie
              animationLink="/animation/404.json"
              :height="300"
              :width="300"
            />
          </client-only>
          <h2 class="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Oops! Page Not Found
          </h2>
          <p class="text-xl text-gray-600 max-w-2xl mx-auto mb-10">
            The page you're looking for seems to have wandered off into the
            academic wilderness. Don't worry, we've got plenty of other
            resources for you.
          </p>
        </div>

        <!-- CTA Section -->
        <div class="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-10">
          <div
            class="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-all"
          >
            <Icon
              name="heroicons:home"
              class="w-8 h-8 text-primary mx-auto mb-3"
            />
            <h3 class="font-semibold text-gray-900 mb-2">Return Home</h3>
            <p class="text-gray-600 text-sm mb-4">
              Go back to our homepage and start exploring academic events
            </p>
            <NuxtLink
              to="/"
              class="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 transition-all w-full"
            >
              Homepage
            </NuxtLink>
          </div>

          <div
            class="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-all"
          >
            <Icon
              name="heroicons:magnifying-glass"
              class="w-8 h-8 text-primary mx-auto mb-3"
            />
            <h3 class="font-semibold text-gray-900 mb-2">Explore Events</h3>
            <p class="text-gray-600 text-sm mb-4">
              Browse our collection of academic events and opportunities
            </p>
            <NuxtLink
              to="/explore"
              class="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 transition-all w-full"
            >
              Explore
            </NuxtLink>
          </div>

          <div
            class="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-all"
          >
            <Icon
              name="heroicons:envelope"
              class="w-8 h-8 text-primary mx-auto mb-3"
            />
            <h3 class="font-semibold text-gray-900 mb-2">Contact Us</h3>
            <p class="text-gray-600 text-sm mb-4">
              Need help finding something? Our team is ready to assist
            </p>
            <NuxtLink
              to="/about/contact"
              class="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 transition-all w-full"
            >
              Get Help
            </NuxtLink>
          </div>
        </div>
      </div>
    </main>
  </NuxtLayout>
</template>

<script setup>
const router = useRouter();
const config = useRuntimeConfig();

// SEO Configuration
useHead({
  title: "404 - Page Not Found | Academic Alerts",
  meta: [
    {
      name: "description",
      content:
        "Sorry, we couldn't find the page you're looking for. Browse our homepage for academic events and updates.",
    },
    { name: "robots", content: "noindex, follow" },
    // Open Graph
    { property: "og:title", content: "404 - Page Not Found | Academic Alerts" },
    {
      property: "og:description",
      content:
        "Sorry, we couldn't find the page you're looking for. Browse our homepage for academic events and updates.",
    },
    { property: "og:type", content: "website" },
    { property: "og:url", content: `${config.public.siteUrl}/404` },
    // Twitter
    { name: "twitter:card", content: "summary" },
    {
      name: "twitter:title",
      content: "404 - Page Not Found | Academic Alerts",
    },
    {
      name: "twitter:description",
      content:
        "Sorry, we couldn't find the page you're looking for. Browse our homepage for academic events and updates.",
    },
  ],
  script: [
    {
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "404 - Page Not Found",
        description:
          "Sorry, we couldn't find the page you're looking for. Browse our homepage for academic events and updates.",
        publisher: {
          "@type": "Organization",
          name: "Academic Alerts",
          url: config.public.siteUrl,
        },
      }),
    },
  ],
});
</script>
